import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { merge, Observable, Subject, Subscription } from 'rxjs';
import { filter, map, share, switchMap, withLatestFrom } from 'rxjs/operators';

import { AppleDeviceProfileResolverActions, selectProfileItemByType } from '@era/stores';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngrx/store';
import { PanelComponent, PanelFooterComponent, SkSpinner3Component } from '@rocksoft/cdk';
import { ConfigurationProfile } from '@rocksoft/mdm-sdk-v2';

import { isNotNullOrUndefined } from '@rocksoft/shared';
import { AppleCommandProgressService } from '../../../shared/services/apple-command-progress.service';
import { AppleMdmService } from '../../../shared/services/apple-mdm-service';
import { CommandSpinnerComponent } from '../../../utilities/command-spinner/command-spinner.component';
import { RequestType } from '../../../viewmodels/apple/commands/apple-command.model';
import { CommandProgress } from '../../../viewmodels/apple/signalr/command-context.model';
import { AppleProfileComponent } from '../apple-profile/apple-profile.component';

@Component({
  selector: 'ml-apple-profile-detail',
  templateUrl: './apple-profile-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    PanelComponent,
    SkSpinner3Component,
    AppleProfileComponent,
    PanelFooterComponent,
    CommandSpinnerComponent,
    FaIconComponent,
    AsyncPipe,
  ],
})
export class AppleProfileDetailComponent implements OnDestroy {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly mdmService = inject(AppleMdmService);
  private readonly commandProgressService = inject(AppleCommandProgressService);

  private _removeProfilesProgress: Subject<CommandProgress> = new Subject<CommandProgress>();

  readonly removeProfilesProgress$: Observable<CommandProgress>;

  readonly profile$: Observable<ConfigurationProfile | undefined>;
  readonly udid$: Observable<string>;

  static navigationHandledCommands = new Set<string>();

  private removeProfilesProgressSubscription: Subscription;

  constructor() {
    const constructorCalledAt = new Date();

    const parentRoute = this.route.parent;
    const grandParentRoute = parentRoute?.parent;

    if (!parentRoute || !grandParentRoute) {
      throw new Error('Expected route to have both parent and grandparent routes.');
    }

    this.udid$ = grandParentRoute.paramMap.pipe(
      map((p) => p.get('udid')),
      filter(isNotNullOrUndefined)
    );

    this.profile$ = this.route.paramMap.pipe(
      map((p) => p.get('payloadUuid')),
      filter(isNotNullOrUndefined),
      switchMap((payloadUuid) => this.store.select(selectProfileItemByType('configuration-profile', payloadUuid)))
    );

    this.removeProfilesProgress$ = this.udid$.pipe(
      switchMap((udid) =>
        merge(
          this._removeProfilesProgress,
          this.commandProgressService.commandsOfRequestType(udid, 'RemoveProfile')
        ).pipe(share())
      )
    );

    this.removeProfilesProgressSubscription = this.removeProfilesProgress$
      .pipe(
        // Make sure the command progress is for the current device
        withLatestFrom(this.udid$),
        filter(([cp, udid]) => cp.udid.trim().toLowerCase() == udid.trim().toLowerCase()),
        // We only want to handle the case where the command completed
        filter(([cp]) => cp.status === 'Acknowledged'),
        // Make sure this is not some old command, but one created as a result of this page
        filter(([cp]) => {
          const val = cp.createdAt == undefined || cp.createdAt.valueOf() > constructorCalledAt.valueOf();
          return val;
        }),
        // We only want to navigate for each command once, globally
        filter(([cp]) => {
          const commandUuid = cp.commandUUID.trim().toLowerCase();
          if (AppleProfileDetailComponent.navigationHandledCommands.has(commandUuid)) {
            return false;
          }

          AppleProfileDetailComponent.navigationHandledCommands.add(commandUuid);
          return true;
        })
      )
      .subscribe(([, udid]) => {
        // Navigate back, since the current profile is no longer available
        void this.router.navigate([], { relativeTo: this.route.parent });

        // Reload profiles
        this.store.dispatch(
          AppleDeviceProfileResolverActions.profileRequested({
            udid,
            profileType: 'configuration-profile',
          })
        );
      });
  }

  ngOnDestroy(): void {
    if (this.removeProfilesProgressSubscription) {
      this.removeProfilesProgressSubscription.unsubscribe();
    }
  }

  public remove(udid: string, profile: ConfigurationProfile): void {
    this._removeProfilesProgress.next(this.createInitialCommandProgress(udid, 'RemoveProfile'));

    this.mdmService.removeProfile(udid, profile.payloadIdentifier).subscribe();
  }

  private createInitialCommandProgress(udid: string, requestType: RequestType): CommandProgress {
    return {
      udid,
      commandUUID: '',
      status: 'Sending',
      requestType,
    };
  }
}
